import { Spinner, toast } from "@veritrellis/ui";
import { useEffect } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { useWorkspaceId } from "../hooks/useWorkspaceId.js";

/**
 * Public marketing CTA target: `/checkout?price_id=price_…` from the marketing site.
 * Resolves the active workspace, kicks a Stripe Checkout session, and redirects.
 */
export const CheckoutRedirectPage = () => {
  const [params] = useSearchParams();
  const priceId = params.get("price_id");
  const { workspaceId, isLoading } = useWorkspaceId();

  useEffect(() => {
    if (isLoading || workspaceId === null || priceId === null) {
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await apiFetch(`/admin/workspaces/${workspaceId}/billing/checkout-session`, {
          method: "POST",
          body: JSON.stringify({ priceId })
        });
        if (cancelled) return;
        if (!res.ok) {
          const err = (await res.json().catch(() => ({}))) as { error?: string };
          toast.show({
            title: "Checkout unavailable",
            message: err.error ?? "Could not start checkout",
            color: "red"
          });
          window.location.assign("/settings/billing");
          return;
        }
        const json = (await res.json()) as { url: string };
        window.location.assign(json.url);
      } catch {
        if (!cancelled) {
          toast.show({
            title: "Checkout failed",
            message: "Network error",
            color: "red"
          });
          window.location.assign("/settings/billing");
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, priceId, isLoading]);

  if (priceId === null) {
    return <Navigate to="/settings/billing" replace />;
  }
  if (!isLoading && workspaceId === null) {
    const onboarding =
      priceId !== null ? `/onboarding?price_id=${encodeURIComponent(priceId)}` : "/onboarding";
    return <Navigate to={onboarding} replace />;
  }
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <div className="flex flex-col items-center gap-3">
        <Spinner />
        <p className="text-sm text-muted-foreground">Starting checkout…</p>
        <p className="max-w-md text-center text-xs text-muted-foreground">
          Veritrellis is a B2B service. Prices exclude VAT unless stated otherwise. Subscriptions
          renew automatically until cancelled. Use is subject to Terms, Privacy, and DPA at
          veritrellis.ai/legal.
        </p>
      </div>
    </div>
  );
};
